import { FC } from "react";
import { Column } from "react-table";
import { Table } from "../../../../common/components/Table/Table";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { useGetPayoutsReportQuery } from "services/admin/adminReportsApi";
import { IPayoutItem } from "common/models/admin/IReports";
import { formatMoney } from "common/utils/helpers";
import { founderName } from "./CreditTable";

const sumColumn = (key: keyof IPayoutItem, money = true) => (info: any) => {
  const total = info.rows.reduce(
    (sum: number, row: any) => sum + Number(row.original[key] ?? 0),
    0
  );
  return <>{money ? formatMoney(total) : total}</>;
};

// «Мы должны»: компании-исполнители. Начислено — вознаграждения, зачисленные
// на баланс после одобрения выплаты; ожидает — по завершённым заказам без
// одобрения; долг — баланс плюс ожидающие начисления
export const PayoutsTable: FC<{ query?: string }> = ({ query }) => {
  const { data, isLoading, isFetching, error } = useGetPayoutsReportQuery({
    query: query || undefined,
  });

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {data?.items.length ? (
        <Table data={data.items} columns={columns} isLoading={isFetching} />
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const columns: Column<IPayoutItem>[] = [
  {
    Header: "#",
    accessor: "id",
    Footer: "Итого",
  },
  {
    Header: "Название организации",
    accessor: "name",
  },
  {
    Header: "ФИО руководителя",
    accessor: "founder",
    Cell: ({ row }: any) => <span>{founderName(row.original.founder)}</span>,
  },
  {
    Header: "Завершённые заказы",
    accessor: "finished_orders_count",
    Footer: sumColumn("finished_orders_count", false),
  },
  {
    Header: "Вознаграждение, руб.",
    accessor: "orders_sum",
    Footer: sumColumn("orders_sum"),
    Cell: ({ row }: any) => <>{formatMoney(row.original.orders_sum)}</>,
  },
  {
    Header: "Начислено на баланс, руб.",
    accessor: "paid",
    Footer: sumColumn("paid"),
    Cell: ({ row }: any) => <>{formatMoney(row.original.paid)}</>,
  },
  {
    Header: "Ожидает одобрения, руб.",
    accessor: "awaiting",
    Footer: sumColumn("awaiting"),
    Cell: ({ row }: any) => <>{formatMoney(row.original.awaiting)}</>,
  },
  {
    Header: "Баланс, руб.",
    accessor: "balance",
    Footer: sumColumn("balance"),
    Cell: ({ row }: any) => (
      <span
        className={row.original.balance > 0 ? "text-primary" : "text-red"}
      >
        {formatMoney(row.original.balance)}
      </span>
    ),
  },
  {
    Header: "Мы должны, руб.",
    accessor: "debt",
    Footer: (info: any) => (
      <span className="text-red">{sumColumn("debt")(info)}</span>
    ),
    Cell: ({ row }: any) => (
      <span className={row.original.debt > 0 ? "text-red" : ""}>
        {formatMoney(row.original.debt)}
      </span>
    ),
  },
];
